import * as React from "react"
import { wasPaymentSubmitted } from "../bookingSession"
import { confirmationPresentation } from "../confirmationPresentation"
import { trackActivity } from "../activity"
import { jsonFetch } from "../bookingApi"
import { draftHasCompleteDetails } from "../bookingDraft"
import { BOOKING_DRAFT_KEY, BOOKING_DRAFT_TTL_MS, BOOKING_SESSION_CLEARED_EVENT, clearBookingSessionStorage, clearCheckoutSessionStorage } from "../bookingSession"
import { toDateOnly } from "../format"
import type { BookingDraft, BookingDraftPayload, BookingStatus } from "../types"
import type { useCheckoutModel } from "./useCheckoutModel"
import type { useCheckoutNavigation } from "./useCheckoutNavigation"
import type { useCheckoutState } from "./useCheckoutState"

type Inputs = Pick<ReturnType<typeof useCheckoutState>,
    "initialDraftRef" | "product" | "setRestoredCheckoutPhase" | "setRestoredCheckoutStatus" | "setBookingStatus" | "setDraftRestored" | "setPhase" | "restoredCheckoutRetry" | "draftRestored" | "restoredCheckoutStatus" | "setQuote" | "setPostcode" | "setConfirmationToken" | "setClientSecret" | "setSavedDetailsFingerprint" | "detailsFingerprint" | "draftPersistenceDisabled" | "draftSaveTimer" | "draftExpiryTimer" | "activeSlug" | "phase" | "quote" | "clientSecret" | "confirmationToken" | "savedDetailsFingerprint" | "quantity" | "range" | "postcode" | "bookingPane" | "selectedAddonIds" | "customer" | "selectedAddressId" | "selectedDeliveryAddress" | "manualDeliveryEntry" | "manualDeliveryAddress" | "differentBilling" | "billingAddress" | "billingComplete" | "lastDraftFingerprint" | "draftExpiresAt" | "journeyStep">
    & Pick<ReturnType<typeof useCheckoutModel>,
        "baseUrl" | "calendarReady" | "calendarRangeError">
    & Pick<ReturnType<typeof useCheckoutNavigation>,
        "navigateWorkflow">
    & { pollStatus: (token: string) => Promise<void> }

export function useBookingDraft(inputs: Inputs) {
    const {
        initialDraftRef,
        product,
        setRestoredCheckoutPhase,
        baseUrl,
        setRestoredCheckoutStatus,
        setBookingStatus,
        setDraftRestored,
        setPhase,
        restoredCheckoutRetry,
        draftRestored,
        calendarReady,
        calendarRangeError,
        restoredCheckoutStatus,
        setQuote,
        setPostcode,
        setConfirmationToken,
        setClientSecret,
        setSavedDetailsFingerprint,
        detailsFingerprint,
        navigateWorkflow,
        draftPersistenceDisabled,
        draftSaveTimer,
        draftExpiryTimer,
        activeSlug,
        phase,
        quote,
        clientSecret,
        confirmationToken,
        savedDetailsFingerprint,
        quantity,
        range,
        postcode,
        bookingPane,
        selectedAddonIds,
        customer,
        selectedAddressId,
        selectedDeliveryAddress,
        manualDeliveryEntry,
        manualDeliveryAddress,
        differentBilling,
        billingAddress,
        billingComplete,
        lastDraftFingerprint,
        draftExpiresAt,
        journeyStep,
        pollStatus,
    } = inputs
    React.useEffect(() => {
        const draft = initialDraftRef.current
        if (!product || !draft?.checkout || draft.completedStep === "none") return
        const controller = new AbortController()
        setRestoredCheckoutPhase("loading")
        jsonFetch<BookingStatus>(`${baseUrl}/v1/booking-status`, {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ confirmationToken: draft.checkout.confirmationToken }), signal: controller.signal,
        }).then(async (status) => {
            if (controller.signal.aborted) return
            setRestoredCheckoutStatus(status.status)
            const outcome = confirmationPresentation(status)
            const submitted = wasPaymentSubmitted(draft.checkout!.confirmationToken)
            if (submitted || outcome.outcome === "unknown" || status.status === "confirmed" || status.status === "manual_review" || ["paid", "refunded", "partially_refunded"].includes(status.paymentStatus)) {
                // Submitted/uncertain payments must not restore an enabled Pay
                // button. Paid bookings remain valid when their dates close.
                setBookingStatus(status)
                if (outcome.successfulPayment) {
                    trackActivity("confirmation_viewed", {}, true)
                    clearBookingSessionStorage()
                }
                setConfirmationToken(draft.checkout!.confirmationToken)
                setDraftRestored(true)
                setPhase("complete")
                if (!outcome.final) void pollStatus(draft.checkout!.confirmationToken)
                setRestoredCheckoutPhase("ready")
                return
            }
            if (status.status === "payment_pending" && draft.checkout?.nameCollection !== "payment") {
                // Old sessions require a separate shipping name. Release their hold
                // before starting a session that collects the name in Stripe.
                await jsonFetch(`${baseUrl}/v1/cancel-checkout`, {
                    method: "POST", headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ confirmationToken: draft.checkout?.confirmationToken }), signal: controller.signal,
                })
                if (controller.signal.aborted) return
                initialDraftRef.current = { ...draft, checkout: null, quote: null, completedStep: "none" }
                clearCheckoutSessionStorage()
                setRestoredCheckoutStatus("expired")
            }
            setRestoredCheckoutPhase("ready")
        }).catch(() => {
            if (!controller.signal.aborted) setRestoredCheckoutPhase("error")
        })
        return () => controller.abort()
    }, [baseUrl, product, restoredCheckoutRetry, pollStatus])

    React.useEffect(() => {
        if (!product || draftRestored || !calendarReady) return
        if (calendarRangeError) {
            clearCheckoutSessionStorage()
            setDraftRestored(true)
            return
        }
        const draft = initialDraftRef.current
        const savedQuote = draft?.quote ?? null
        const savedCheckout = draft?.checkout ?? null
        const resumableCheckout = restoredCheckoutStatus === "payment_pending" && savedQuote
            && savedCheckout?.clientSecret
            && savedCheckout.confirmationToken
            && savedQuote.product.slug === product.slug
            && savedQuote.expiresAt
            && new Date(savedQuote.expiresAt).getTime() > Date.now()

        if (draft && savedQuote && savedCheckout && resumableCheckout) {
            setQuote(savedQuote)
            setPostcode(savedQuote.postcode)
            setConfirmationToken(savedCheckout.confirmationToken)
            setClientSecret(savedCheckout.clientSecret)
            window.sessionStorage.setItem("arctic-hire:pending-confirmation-token", savedCheckout.confirmationToken)
            const detailsSaved = draft.completedStep === "details" && draftHasCompleteDetails(draft)
            if (detailsSaved) setSavedDetailsFingerprint(detailsFingerprint)
            navigateWorkflow("payment", "replace")
        } else {
            clearCheckoutSessionStorage()
            navigateWorkflow("dates", "replace")
        }
        setDraftRestored(true)
    }, [calendarRangeError, calendarReady, detailsFingerprint, draftRestored, navigateWorkflow, product, restoredCheckoutStatus])

    React.useEffect(() => {
        const disablePersistence = () => {
            draftPersistenceDisabled.current = true
            if (draftSaveTimer.current !== null) window.clearTimeout(draftSaveTimer.current)
            if (draftExpiryTimer.current !== null) window.clearTimeout(draftExpiryTimer.current)
        }
        window.addEventListener(BOOKING_SESSION_CLEARED_EVENT, disablePersistence)
        return () => window.removeEventListener(BOOKING_SESSION_CLEARED_EVENT, disablePersistence)
    }, [])

    React.useEffect(() => {
        if (!draftRestored || draftPersistenceDisabled.current || !activeSlug || phase === "complete") return
        if (draftSaveTimer.current !== null) window.clearTimeout(draftSaveTimer.current)
        draftSaveTimer.current = window.setTimeout(() => {
            const checkout = quote && clientSecret && confirmationToken ? { clientSecret, confirmationToken, nameCollection: "payment" as const } : null
            const completedStep: BookingDraft["completedStep"] = checkout
                ? savedDetailsFingerprint === detailsFingerprint ? "details" : "booking"
                : "none"
            const payload: BookingDraftPayload = {
                version: 1,
                productSlug: activeSlug,
                quantity,
                deliveryDate: range?.from ? toDateOnly(range.from) : undefined,
                collectionDate: range?.to ? toDateOnly(range.to) : undefined,
                postcode,
                bookingPane,
                selectedAddonIds,
                completedStep,
                customer,
                selectedAddressId,
                selectedDeliveryAddress,
                manualDeliveryEntry,
                manualDeliveryAddress,
                differentBilling,
                billingAddress,
                billingComplete,
                quote: checkout ? quote : null,
                checkout,
            }
            const fingerprint = JSON.stringify(payload)
            const changed = fingerprint !== lastDraftFingerprint.current
            if (changed || !window.sessionStorage.getItem(BOOKING_DRAFT_KEY)) {
                const updatedAt = Date.now()
                try { window.sessionStorage.setItem(BOOKING_DRAFT_KEY, JSON.stringify({ ...payload, updatedAt })) } catch { /* Browser storage may be unavailable. */ }
                lastDraftFingerprint.current = fingerprint
                draftExpiresAt.current = updatedAt + BOOKING_DRAFT_TTL_MS
            }
            if (draftExpiryTimer.current !== null) window.clearTimeout(draftExpiryTimer.current)
            const remaining = Math.max(0, draftExpiresAt.current - Date.now())
            draftExpiryTimer.current = window.setTimeout(() => {
                window.sessionStorage.removeItem(BOOKING_DRAFT_KEY)
            }, remaining)
        }, 100)
        return () => {
            if (draftSaveTimer.current !== null) window.clearTimeout(draftSaveTimer.current)
        }
    }, [activeSlug, billingAddress, billingComplete, bookingPane, clientSecret, confirmationToken, customer, detailsFingerprint, differentBilling, draftRestored, journeyStep, savedDetailsFingerprint, manualDeliveryAddress, manualDeliveryEntry, phase, postcode, quantity, quote, range, selectedAddressId, selectedAddonIds, selectedDeliveryAddress])
}
